// src/pages/invoices/components/InvoiceSummary.tsx
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { currencySymbols, GstType } from '@/lib/constants/invoice';
import {
  calculateTaxBreakdown,
  formatNumberWithCommas,
  numberToWords,
  parseTaxValue,
} from '@/lib/utils/invoice';
import { InvoiceItem } from '@/types/invoice.types';
import { Calculator, Receipt } from 'lucide-react';
import React, { useMemo } from 'react';

// Summary Component
interface InvoiceSummaryProps {
  items: InvoiceItem[];
  currency: string;
  isCustomerTaxable: boolean;
  taxType: GstType;
}

const InvoiceSummary: React.FC<InvoiceSummaryProps> = ({
  items,
  currency,
  isCustomerTaxable,
  taxType,
}) => {
  const currencySymbol = currencySymbols[currency] || currency;

  const totals = useMemo(() => {
    const subtotal = items.reduce((sum, item) => sum + item.rate * item.quantity, 0);

    const taxBreakdown = calculateTaxBreakdown(items, taxType);
    const totalTax = taxBreakdown.reduce((sum, line) => sum + line.amount, 0);
    let taxableItemsCount = 0;
    let nonTaxableItemsCount = 0;
    let zeroRatedItemsCount = 0;

    items.forEach((item) => {
      if ((item.taxRate || 0) > 0) {
        taxableItemsCount++;
      } else if (parseTaxValue(item.taxValue, taxType).gstType) {
        zeroRatedItemsCount++;
      } else if (item.taxValue) {
        nonTaxableItemsCount++;
      }
    });

    const totalAmount = subtotal + totalTax;
    const amountInWords = numberToWords(totalAmount, currency);

    return {
      subtotal,
      totalTax,
      totalAmount,
      amountInWords,
      taxBreakdown,
      taxableItemsCount,
      nonTaxableItemsCount,
      zeroRatedItemsCount,
      itemCount: items.length,
      hasValidItems: items.some((item) => item.itemId),
    };
  }, [items, currency, taxType]);

  const formatAmount = (amount: number) =>
    `${currencySymbol}${formatNumberWithCommas(amount.toFixed(2))}`;

  // Determine tax display status
  const getTaxStatus = () => {
    if (!isCustomerTaxable) {
      return { type: 'non-taxable', label: 'Customer Non-Taxable' };
    }
    if (totals.nonTaxableItemsCount > 0 && totals.taxableItemsCount === 0) {
      return { type: 'exempt', label: 'All Items Exempt' };
    }
    if (totals.zeroRatedItemsCount > 0 && totals.taxableItemsCount === 0) {
      return { type: 'zero-rated', label: 'Zero Rated' };
    }
    if (totals.taxableItemsCount === 0 && totals.hasValidItems) {
      return { type: 'no-tax', label: 'No Tax Applied' };
    }
    return { type: 'taxable', label: '' };
  };

  const taxStatus = getTaxStatus();

  if (!totals.hasValidItems) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-8">
          <div className="flex flex-col items-center justify-center text-center text-muted-foreground">
            <Calculator className="h-10 w-10 mb-3 opacity-50" />
            <p className="font-medium">No items added yet</p>
            <p className="text-sm mt-1">Add items to see the invoice summary</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center gap-2 mb-4">
          <Receipt className="h-5 w-5 text-muted-foreground" />
          <h3 className="text-lg font-medium">Invoice Summary</h3>
          {totals.itemCount > 0 && (
            <Badge variant="secondary" className="ml-auto">
              {totals.itemCount} item{totals.itemCount > 1 ? 's' : ''}
            </Badge>
          )}
        </div>

        <div className="space-y-3">
          {/* Subtotal */}
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Subtotal</span>
            <span className="font-medium tabular-nums">{formatAmount(totals.subtotal)}</span>
          </div>

          <Separator />

          {/* Tax Section */}
          <div className="space-y-2">
            {/* Tax Status Badge */}
            {taxStatus.type !== 'taxable' && (
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Tax Status</span>
                <Badge
                  variant="outline"
                  className={
                    taxStatus.type === 'non-taxable'
                      ? 'border-gray-300 text-gray-600'
                      : taxStatus.type === 'exempt'
                        ? 'border-amber-300 text-amber-700 bg-amber-50'
                        : taxStatus.type === 'zero-rated'
                          ? 'border-blue-300 text-blue-700 bg-blue-50'
                          : 'border-gray-300 text-gray-600'
                  }
                >
                  {taxStatus.label}
                </Badge>
              </div>
            )}

            {/* GST Breakdown - Show when there are taxes */}
            {totals.taxBreakdown.length > 0 ? (
              <>
                {totals.taxBreakdown.map((line) => (
                  <div
                    key={`${line.label}-${line.rate}`}
                    className="flex justify-between items-center"
                  >
                    <span className="text-muted-foreground">
                      {line.label} @ {line.rate}%
                    </span>
                    <span className="font-medium tabular-nums text-green-600">
                      + {formatAmount(line.amount)}
                    </span>
                  </div>
                ))}

                {/* Total Tax - Only show if multiple tax lines */}
                {totals.taxBreakdown.length > 1 && (
                  <div className="flex justify-between items-center pt-1 border-t border-dashed">
                    <span className="text-muted-foreground font-medium">Total Tax</span>
                    <span className="font-medium tabular-nums text-green-600">
                      + {formatAmount(totals.totalTax)}
                    </span>
                  </div>
                )}
              </>
            ) : (
              // No tax scenario
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Tax</span>
                <span className="tabular-nums text-muted-foreground">
                  {isCustomerTaxable ? formatAmount(0) : '—'}
                </span>
              </div>
            )}
          </div>

          <Separator />

          {/* Grand Total */}
          <div className="flex justify-between items-center py-2">
            <span className="text-lg font-semibold">Total Amount</span>
            <span className="text-xl font-bold tabular-nums text-primary">
              {formatAmount(totals.totalAmount)}
            </span>
          </div>

          {/* Amount in Words */}
          {totals.totalAmount > 0 && (
            <div className="bg-muted/50 rounded-lg p-3 mt-2">
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                Amount in Words
              </p>
              <p className="text-sm font-medium">{totals.amountInWords}</p>
            </div>
          )}

          {/* Tax Summary Indicators */}
          {(totals.taxableItemsCount > 0 ||
            totals.nonTaxableItemsCount > 0 ||
            totals.zeroRatedItemsCount > 0) && (
            <div className="flex flex-wrap gap-2 pt-2">
              {totals.taxableItemsCount > 0 && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  <span>
                    {totals.taxableItemsCount} taxable item
                    {totals.taxableItemsCount > 1 ? 's' : ''}
                  </span>
                </div>
              )}
              {totals.zeroRatedItemsCount > 0 && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                  <span>
                    {totals.zeroRatedItemsCount} zero-rated item
                    {totals.zeroRatedItemsCount > 1 ? 's' : ''}
                  </span>
                </div>
              )}
              {totals.nonTaxableItemsCount > 0 && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                  <span>
                    {totals.nonTaxableItemsCount} exempt item
                    {totals.nonTaxableItemsCount > 1 ? 's' : ''}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default InvoiceSummary;

{
  /* Summary Section */
}
{
  /* <div className="border-t pt-6">
  <h3 className="text-lg font-medium mb-4">Summary</h3>
  <div className="space-y-2 max-w-sm ml-auto">
    <div className="flex justify-between">
      <span className="text-gray-600">Subtotal:</span>
      <span className="font-medium">
        {currencySymbols[values.currency] || ''}
        {totals.subTotal.toFixed(2)}
      </span>
    </div>

    {isCustomerTaxable &&
      Object.entries(totals.taxBreakdown).map(
        ([rate, amount]) =>
          amount > 0 && (
            <div key={rate} className="flex justify-between">
              <span className="text-gray-600">GST ({rate}%):</span>
              <span className="font-medium">
                {currencySymbols[values.currency] || ''}
                {amount.toFixed(2)}
              </span>
            </div>
          )
      )}

    {totals.taxTotal > 0 && (
      <div className="flex justify-between">
        <span className="text-gray-600">Total Tax:</span>
        <span className="font-medium">
          {currencySymbols[values.currency] || ''}
          {totals.taxTotal.toFixed(2)}
        </span>
      </div>
    )}

    <div className="border-t pt-2">
      <div className="flex justify-between">
        <span className="text-lg font-bold">Total:</span>
        <span className="text-lg font-bold">
          {currencySymbols[values.currency] || ''}
          {totals.totalAmount.toFixed(2)}
        </span>
      </div>
    </div>

    {totals.totalAmount > 0 && (
      <div className="border-t pt-2">
        <p className="text-sm text-gray-600">
          <strong>Amount in Words:</strong> {totals.amountInWords}
        </p>
      </div>
    )}
  </div>
</div>; */
}
